import React from 'react';
import { NavLink } from 'react-router-dom';
import { useRunRouteLoader } from '../hooks/useRunRouteLoader';

/**
 * This component can be used to prefetch route data when the link is rendered.
 *
 * This component wraps React Router's Link and automatically triggers a `fetcher.load()`
 * call when the component mounts. This calls the route's `loader` function.
 *
 * **IMPORTANT**: For prefetching to work, the target route MUST have a loader function
 * defined that contains the data fetching logic.
 * Otherwise, this will have no effect.
 *
 * https://reactrouter.com/start/data/data-loading
 *
 * Callers that need to toggle prefetching at the call site should pick
 * `NavLink` vs `PrefetchingNavLink` themselves rather than passing a flag — a
 * `<PrefetchingNavLink isPrefetching={false} />` would be a contradiction.
 *
 * @param props - All props are passed to react-router-dom's `NavLink` component
 * @returns A `NavLink` component with prefetching behavior
 */
// eslint-disable-next-line react/display-name -- lint-backlog: anonymous forwardRef/memo component; add a displayName in a follow-up
export const PrefetchingNavLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<typeof NavLink>
>((props, ref) => {
  useRunRouteLoader(props.to);

  return <NavLink ref={ref} {...props} />;
});
